/**
 * `<product-form>`: the add-to-cart form shared by quick add and the product page
 * (snippets/product-form.liquid). Every behavior here enhances a form that already posts to
 * /cart/add.js's plain HTML equivalent (/cart/add) on its own.
 *
 * Option change: rebuilds `option_values` from the checked radios' `data-option-value-id` and
 * re-fetches `?section_id=<data-section-id>&option_values=<ids>` on the product (or, for a combined
 * listing value, on the changed radio's own `data-product-url`) through the Section Rendering API.
 * The response is the whole caller's section re-rendered server-side with that selection, so the
 * swap is just Liquid business logic (size preselection, availability) recomputed once more: this
 * only splices the fresh `[data-variant-picker]`, `[data-variant-id]` and `[data-buy-button]` in
 * place, plus `[data-product-price]` and every `[data-variant-region]` wherever they live in that same
 * `.shopify-section` wrapper. It
 * keeps focus on the radio the shopper just picked and fires a `product:variant-change` CustomEvent
 * (bubbling, detail `{ variantId, html }`) for anything else that cares — a product page gallery
 * swaps its own media on that event; this module never touches media. In the "product" context it
 * also mirrors the new variant id into `?variant=` via `history.replaceState`. The resulting buy
 * button label (Add to Cart / Sold Out / Select a Size) is announced through `[data-form-status]`
 * (snippets/product-form.liquid's own `role="status"` region) so the outcome of a change reaches a
 * screen reader even though focus stays on the option the shopper just picked. The change runs inside
 * the standard event `shopify:product:select`, whose variant is read from the product's JSON.
 *
 * A fieldset with no checked radio only ever happens for the size option, in Theme Settings >
 * Products > Size preselection "None" (snippets/size-preselect.liquid: pending). Shopify's own
 * `option_values` resolution always fills in a concrete variant server-side, even when the ids sent
 * don't cover every option, so a fresh response would otherwise show some size checked again. This
 * carries that fieldset's "nothing chosen" state (and its legend/label text, already correctly
 * localized by the prior render) forward across the swap, and forces the buy button back to its
 * disabled "select a size" state to match, so that setting keeps working through a live change.
 *
 * Submit: calls `addToCart` (dynamically imported from `data-cart-url`, the `cart.js` asset)
 * instead of a normal form post; cart.js opens the cart drawer on success. In the "quick-add"
 * context this also closes the ancestor `<dialog>` once the add succeeds. Errors show inline in
 * `[data-form-error]`. Without JS the form posts to /cart/add and the page reloads.
 */

class ProductForm extends HTMLElement {
  #form;
  #variantAbort = null;
  #submitting = false;
  #productJson = null;

  connectedCallback() {
    this.#form = this.querySelector("form");
    this.addEventListener("change", this.#onOptionChange);
    this.addEventListener("click", this.#onQuantityClick);
    this.#form.addEventListener("submit", this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.#onOptionChange);
    this.removeEventListener("click", this.#onQuantityClick);
    this.#form?.removeEventListener("submit", this.#onSubmit);
    this.#variantAbort?.abort();
  }

  #announce(text) {
    const status = this.querySelector("[data-form-status]");
    if (!status || !text) return;
    status.textContent = "";
    requestAnimationFrame(() => {
      status.textContent = text;
    });
  }

  #onQuantityClick = (event) => {
    const control = event.target.closest("[data-quantity-change]");
    if (!control || !this.contains(control)) return;
    const input = this.querySelector("[data-quantity]");
    if (!input) return;
    const next = Math.max(Number(input.value || 0) + Number(control.getAttribute("data-quantity-change")), 1);
    input.value = next;
  };

  #onOptionChange = (event) => {
    const radio = event.target.closest('input[type="radio"][data-option-value-id]');
    if (!radio || !this.contains(radio)) return;
    const { productId: id, productTitle: title, productHandle: handle } = this.dataset;
    const selectedOptions = [...this.querySelectorAll("fieldset[data-option-name]")].flatMap((fieldset) => {
      const value = fieldset.querySelector("input:checked")?.value;
      return value ? [{ name: fieldset.dataset.optionName, value }] : [];
    });
    window.trackStandardEvent(
      this,
      "ProductSelectEvent",
      { product: { id, title, handle }, selectedOptions },
      () => this.#applyVariant(radio),
      () => this.#selectedVariant(),
    );
  };

  async #selectedVariant() {
    this.#productJson ??= fetch(`${this.dataset.productUrl.split("?")[0]}.js`).then((response) => response.json());
    const product = await this.#productJson;
    const id = Number(this.querySelector("[data-variant-id]")?.value);
    const variant = product.variants.find((candidate) => candidate.id === id);
    if (!variant) return { variant: null };
    return {
      variant: {
        id: `gid://shopify/ProductVariant/${id}`,
        title: variant.title,
        availableForSale: variant.available,
        price: { amount: (variant.price / 100).toFixed(2), currencyCode: window.Shopify.currency.active },
        selectedOptions: product.options.map(({ name }, index) => ({ name, value: variant.options[index] })),
      },
    };
  }

  async #applyVariant(changedRadio) {
    const ids = [...this.querySelectorAll('input[type="radio"][data-option-value-id]:checked')].map(
      (input) => input.dataset.optionValueId,
    );
    const base = changedRadio.dataset.productUrl || this.dataset.productUrl;
    const url = new URL(base, window.location.origin);
    url.searchParams.set("section_id", this.dataset.sectionId);
    url.searchParams.set("option_values", ids.join(","));

    this.#variantAbort?.abort();
    const controller = new AbortController();
    this.#variantAbort = controller;

    let html;
    try {
      const response = await fetch(url, { signal: controller.signal, headers: { Accept: "text/html" } });
      if (!response.ok) return;
      html = await response.text();
    } catch (error) {
      if (error.name !== "AbortError") {
        /* Leave the current selection in place; the shopper can still retry or submit as is. */
      }
      return;
    }
    if (controller.signal.aborted) return;

    if (base !== this.dataset.productUrl) this.#productJson = null;
    this.dataset.productUrl = base;
    const doc = new DOMParser().parseFromString(html, "text/html");
    this.#swap(doc, changedRadio.dataset.optionValueId);
  }

  #swap(doc, focusValueId) {
    const currentPicker = this.querySelector("[data-variant-picker]");
    const freshPicker = doc.querySelector("[data-variant-picker]");

    // Positions left with no checked radio before this fetch (see the module doc comment above).
    const pendingFieldsets = currentPicker
      ? [...currentPicker.querySelectorAll("fieldset[data-option-position]")].filter(
          (fieldset) => !fieldset.querySelector("input:checked"),
        )
      : [];

    if (freshPicker) {
      for (const oldFieldset of pendingFieldsets) {
        const freshFieldset = freshPicker.querySelector(
          `fieldset[data-option-position="${oldFieldset.dataset.optionPosition}"]`,
        );
        if (!freshFieldset) continue;
        const freshChecked = freshFieldset.querySelector("input:checked");
        if (freshChecked) freshChecked.checked = false;
        const oldLegend = oldFieldset.querySelector("legend");
        const freshLegend = freshFieldset.querySelector("legend");
        if (oldLegend && freshLegend) freshLegend.textContent = oldLegend.textContent;
        const oldValue = oldFieldset.querySelector(".variant-option__value");
        const freshValue = freshFieldset.querySelector(".variant-option__value");
        if (oldValue && freshValue) freshValue.textContent = oldValue.textContent;
        // "In Stock" beside the size's name describes the size Shopify picked.
        freshFieldset.querySelector("[data-size-stock]")?.remove();
      }
    }
    if (freshPicker && currentPicker) currentPicker.replaceWith(freshPicker);

    const freshId = doc.querySelector("[data-variant-id]");
    const currentId = this.querySelector("[data-variant-id]");
    let variantId = currentId?.value ?? null;
    if (freshId && currentId) {
      currentId.value = freshId.value;
      variantId = freshId.value;
    }

    const oldBuyButton = this.querySelector("[data-buy-button]");
    const freshBuyButton = doc.querySelector("[data-buy-button]");
    if (freshBuyButton && pendingFieldsets.length && oldBuyButton) {
      const freshLabel = freshBuyButton.querySelector("[data-buy-label]");
      const oldLabel = oldBuyButton.querySelector("[data-buy-label]");
      if (freshLabel && oldLabel) freshLabel.textContent = oldLabel.textContent;
      freshBuyButton.disabled = true;
      freshBuyButton.hidden = false;
    }
    if (freshBuyButton && oldBuyButton) oldBuyButton.replaceWith(freshBuyButton);

    const section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const freshPrice = doc.querySelector("[data-product-price]");
    const currentPrice = section?.querySelector("[data-product-price]");
    if (freshPrice && currentPrice) currentPrice.replaceWith(freshPrice);

    // The section's other variant-dependent parts. While a size is still to be chosen they keep
    // their first render: the fresh ones describe a variant Shopify picked, not the shopper.
    if (!pendingFieldsets.length) {
      for (const region of section?.querySelectorAll("[data-variant-region]") ?? []) {
        const fresh = doc.querySelector(`[data-variant-region="${CSS.escape(region.dataset.variantRegion)}"]`);
        if (!fresh) continue;
        // Keep what the shopper already typed (the back-in-stock email).
        for (const input of region.querySelectorAll("input[name]:not([type='hidden'])")) {
          const twin = fresh.querySelector(`input[name="${CSS.escape(input.name)}"]`);
          if (twin && input.type === twin.type && input.type !== "checkbox") twin.value = input.value;
        }
        region.replaceWith(fresh);
      }
    }

    this.#announce(freshBuyButton?.textContent?.trim());

    if (focusValueId) {
      this.querySelector(`[data-option-value-id="${CSS.escape(focusValueId)}"]`)?.focus();
    }

    if (this.dataset.context === "product" && variantId) {
      const next = new URL(window.location.href);
      next.searchParams.set("variant", variantId);
      window.history.replaceState(window.history.state, "", next);
    }

    this.dispatchEvent(
      new CustomEvent("product:variant-change", { bubbles: true, detail: { variantId, html: doc.body.innerHTML } }),
    );
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#submitting) return;
    this.#submitting = true;

    const submitButton = this.#form.querySelector("[data-buy-button]");
    const errorRegion = this.#form.querySelector("[data-form-error]");
    if (errorRegion) {
      errorRegion.textContent = "";
      errorRegion.setAttribute("hidden", "");
    }
    submitButton?.setAttribute("aria-busy", "true");

    try {
      const { addToCart } = await import(this.dataset.cartUrl);
      const formData = new FormData(this.#form);
      await addToCart(formData, this, this.dataset.context === "quick-add" ? "dialog" : "product");
      // Complete Your Kit unticks a piece that was added through quick add (assets/product-loadout.js).
      this.dispatchEvent(new CustomEvent("product:added", { bubbles: true, detail: { variantId: Number(formData.get("id")) } }));
      if (this.dataset.context === "quick-add") {
        this.closest("dialog")?.close();
      }
    } catch (error) {
      if (errorRegion) {
        errorRegion.textContent = error?.data?.description || error?.data?.message || this.dataset.errorGeneric;
        errorRegion.removeAttribute("hidden");
      }
    } finally {
      submitButton?.removeAttribute("aria-busy");
      this.#submitting = false;
    }
  };
}

customElements.define("product-form", ProductForm);
